import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import type { ComponentMessageFileType } from "../../../types/ComponentMessageType";
import { formatBytes } from "@whatslly/commons/utils";
import last from "lodash/last";
import { messagesModals } from "../../../stores/messagesModals";
import { BaseModal, IModalInstance } from "@whatslly/ui-components/modals";
import { AudioPlayer } from "@whatslly/ui-components/AudioPlayer";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { AppIds } from "@whatslly/types/LoginProviderType";
import styles from "../message.module.scss";
import { Highlighted } from "@whatslly/ui-components/Highlighted";
import { SELECTORS } from "@whatslly/ui-components/Pages/TuvisMessaging/services/selectors";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { useTranslation } from "react-i18next";

const PreviewModal: React.FC<IModalInstance & { file: ComponentMessageFileType }> = (props) => {
  const { file } = props;
  const ChildComponent = useCallback(() => {
    if (file.fileType === "video") return <video data-testid="preview-modal-video-player" controls={true} src={file.url} />;
    return (
      <div className={styles.messagePreviewModalImageContainer}>
        <img data-testid="preview-modal-image" src={file.url} alt={file.url} />
      </div>
    );
  }, [file.fileType, file.url]);
  return (
    <BaseModal
      closeOnClickOutside={false}
      bodyClassName={styles.modalBody}
      backdropClassName={styles.videoModalBackdrop}
      closeModal={props.closeModal}
      backdropHeader={
        <div className={styles.actionsContainer}>
          <DownloadButton file={file} />
          <SvgV2
            data-testid={`video-modal-close`}
            className={styles.modalClose}
            width={14}
            height={14}
            iconName={"close"}
            onClick={() => props.closeModal()}
          />
        </div>
      }
      className={styles.videoModalContainer}
    >
      <ChildComponent />
    </BaseModal>
  );
};

const CrmIcon: React.FC<{ crm: LoginProviderType; href: string }> = ({ crm, href }) => {
  return (
    <a href={href} target="_blank" className={classnames(styles.crmIcon)}>
      {crm === AppIds.SALESFORCE ? (
        <SvgV2 width={22} height={22} iconName={`salesforce`} />
      ) : (
        <SvgV2 width={22} height={22} iconName={`microsoft-dynamics`} />
      )}
    </a>
  );
};

const openPreviewModal = (event: React.MouseEvent, file: ComponentMessageFileType) => {
  event.stopPropagation();
  event.preventDefault();
  return messagesModals.openModal(PreviewModal, { file });
};

const PlayButton: React.FC<{ file: ComponentMessageFileType }> = ({ file }) => {
  return (
    <div data-testid="video-without-preview-play-button" onClick={(e) => openPreviewModal(e, file)} className={styles.playFileButton}>
      <SvgV2 width={10} height={10} iconName={"play"} />
    </div>
  );
};

const DownloadButton: React.FC<{ file: ComponentMessageFileType }> = ({ file }) => {
  return (
    <a href={file.url} target="_blank" download={file.title} className={styles.downloadFile}>
      <SvgV2 width={10} height={10} iconName={"arrow-right-short"} />
    </a>
  );
};

const GenericFile: React.FC<{
  file: ComponentMessageFileType;
  Button?: React.FC<{ file: ComponentMessageFileType }>;
  crm?: LoginProviderType;
  withTail: boolean;
}> = ({ file, Button, crm, withTail }) => {
  const fileSuffix = useMemo(() => {
    return last(file.title.split("."));
  }, [file.title]);
  const formattedSize = formatBytes(file.size);

  return (
    <div className={classnames(styles.fileGeneric, { [styles.withTail]: withTail })}>
      <SvgV2 className={styles.fileIcon} width={24} height={30} iconName={"file"} />
      <div>
        <div className={styles.fileName}>{file.title}</div>
        <div className={styles.fileDetails}>
          {fileSuffix && <span>{fileSuffix}</span>}
          {fileSuffix && <span>{`•`}</span>}
          <span>{formattedSize}</span>
        </div>
      </div>
      {crm && <CrmIcon href={file.link} crm={crm} />}
      {Button && <Button file={file} />}
    </div>
  );
};

const MessageImageFile: React.FC<{
  file: ComponentMessageFileType;
  withTail: boolean;
  timeContrast: boolean;
  style?: Record<"img" | "container", React.CSSProperties>;
}> = ({ file, withTail, timeContrast, style }) => {
  return (
    <a
      href={file.link}
      target="_blank"
      onClick={(e) => openPreviewModal(e, file)}
      className={classnames(styles.fileImage, { [styles.withTail]: withTail, [styles.contrastTime]: timeContrast })}
      style={style?.container}
    >
      <img alt="preview-image" src={file.url} style={style?.img} />
    </a>
  );
};

const MessageVideoFile: React.FC<{
  file: ComponentMessageFileType;
  crm?: LoginProviderType;
  withTail: boolean;
  timeContrast: boolean;
  height?: number;
  width?: number;
  style?: Record<"img" | "container", React.CSSProperties>;
}> = ({ file, crm, timeContrast, withTail, height, width, style }) => {
  return (
    <>
      {file.previewUrl ? (
        <a
          data-testid="element-video-file"
          href={file.link}
          target="_blank"
          className={classnames(styles.fileVideoPreview, { [styles.withTail]: withTail, [styles.contrastTime]: timeContrast })}
          style={style?.container}
        >
          <img alt="preview-video" src={file.previewUrl} height={height} width={width} style={style?.img} />
          <div className={styles.playButtonContainer}>
            <div data-testid="msg-video-play-button" onClick={(e) => openPreviewModal(e, file)} className={styles.playButton}>
              <SvgV2 width={23} height={23} iconName={"play"} />
            </div>
          </div>
        </a>
      ) : (
        <GenericFile withTail={withTail} crm={crm} Button={PlayButton} file={file} />
      )}
    </>
  );
};

const MessageAudioFile: React.FC<{ file: ComponentMessageFileType; withTail: boolean; searchText?: string }> = ({ file, withTail, searchText }) => {
  return (
    <div className={classnames(styles.audioPlayerWrapper, { [styles.withTail]: withTail })}>
      <AudioPlayer audioSrc={file.url} />
      {file.transcribeText && (
        <Highlighted
          className={styles.messageText}
          text={file.transcribeText}
          highlight={searchText ?? ""}
          testid={`msg-audio-file-${file.id}`}
          tuvisId={SELECTORS.MESSAGE_BODY}
        />
      )}
    </div>
  );
};

export const MessageFile: React.FC<{
  file: ComponentMessageFileType;
  crm?: LoginProviderType;
  withTail: boolean;
  timeContrast: boolean;
  height?: number;
  width?: number;
  imgStyle?: Record<"img" | "container", React.CSSProperties>;
  searchText?: string;
}> = ({ file, crm, timeContrast, withTail, height, width, imgStyle, searchText }) => {
  const { t } = useTranslation();

  const fileSuffix = useMemo(() => {
    return last(file.title.split("."));
  }, [file.title]);
  const formattedSize = formatBytes(file.size);

  // Check if file has any invalid flag
  if (file.invalid === "size" || file.invalid === "messageType" || file.invalid === "mimeType") {
    const tooltipText = t(`file_invalid.${file.invalid}`, {
      defaultValue: "File cannot be synced",
    });

    return (
      <div className={classnames(styles.fileGeneric, { [styles.withTail]: withTail })}>
        <SvgV2 className={styles.fileIcon} width={24} height={30} iconName={"file"} />
        <div className={styles.fileContent}>
          <div className={styles.fileName}>{file.title}</div>
          <div className={styles.fileDetails}>
            {fileSuffix && <span>{fileSuffix}</span>}
            {fileSuffix && <span>{`•`}</span>}
            <span>{formattedSize}</span>
          </div>
        </div>
        <Tooltip delay={300} value={tooltipText} placement="top">
          <div className={styles.warningButton}>
            <SvgV2 width={24} height={24} iconName={"warning"} />
          </div>
        </Tooltip>
      </div>
    );
  }

  // Regular files
  if (file.fileType === "image" || file.fileType === "sticker") {
    return <MessageImageFile timeContrast={timeContrast} withTail={withTail} file={file} style={imgStyle} />;
  }
  if (file.fileType === "video") {
    return (
      <MessageVideoFile
        timeContrast={timeContrast}
        withTail={withTail}
        crm={crm}
        file={file}
        height={height || Number(String(imgStyle?.img.height)?.replace("px", ""))}
        width={width || Number(String(imgStyle?.img.width)?.replace("px", ""))}
        style={imgStyle}
      />
    );
  }
  const isCallRecording = file.fileType === "call_recording" || file.fileType === "call";
  if (file.fileType === "audio" || isCallRecording) {
    return (
      <>
        <MessageAudioFile withTail={withTail} file={file} searchText={searchText} />
        {isCallRecording && (
          <Tooltip delay={300} value={t("channels.mobile_call_recording")} placement="top">
            <div className={styles.callRecordingIndicator}>
              <SvgV2 width={12} height={12} iconName={"call_recording"} />
            </div>
          </Tooltip>
        )}
      </>
    );
  }
  return <GenericFile withTail={withTail} crm={crm} Button={DownloadButton} file={file} />;
};
